import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../Configuration/firebase";
import {
  sendSafetyAlert,
  recordCheckinHistory,
  setActiveCheckin,
  fetchCheckinHistory,
} from "../utils/safety";
import "../CSS/App.css";

// Emergency alert is parked for now - hidden from the UI. The handler and SMS
// path are left intact behind this flag so it's a one-line flip to bring back.
const EMERGENCY_ENABLED = false;

function formatTime(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

// Safety Check-In hub: emergency alert, check-in / check-out, and history.
const Safety = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [uid, setUid] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [activeCheckin, setActive] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async (userId) => {
    const userSnap = await getDoc(doc(db, "users", userId));
    const data = userSnap.exists() ? userSnap.data() : {};
    setContacts(data.trustedContacts || []);
    setActive(data.activeCheckin || null);

    const addrSnap = await getDocs(collection(db, "users", userId, "addresses"));
    const list = addrSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setAddresses(list);

    // Preselect the address the proximity nudge sent us to, else the first.
    const nudgeId = location.state?.addressId;
    if (nudgeId && list.some((a) => a.id === nudgeId)) setSelectedId(nudgeId);
    else if (list.length > 0) setSelectedId((prev) => prev || list[0].id);

    setHistory(await fetchCheckinHistory(userId));
  }, [location.state]);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      setUid(user.uid);
      try {
        await load(user.uid);
      } catch (err) {
        console.error("Error loading safety data:", err);
        setError("Couldn't load your safety info. Please try again.");
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [navigate, load]);

  const noContacts = contacts.length === 0;

  const runAlert = async (type, address) => {
    const result = await sendSafetyAlert({
      type,
      addressTitle: address?.Title || "",
      address: address?.Address || "",
    });
    await recordCheckinHistory(uid, {
      type,
      addressTitle: address?.Title || "",
      address: address?.Address || "",
      smsSent: result.sent || 0,
    });
    return result;
  };

  const handleCheckIn = async () => {
    const address = addresses.find((a) => a.id === selectedId);
    if (!address) {
      setError("Pick an address to check in at.");
      return;
    }
    setBusy("checkin");
    setError("");
    setMessage("");
    try {
      const result = await runAlert("checkin", address);
      const active = {
        addressTitle: address.Title,
        address: address.Address,
        startedAt: Date.now(),
      };
      await setActiveCheckin(uid, active);
      setActive(active);
      setMessage(`Checked in. ${result.sent || 0} contact(s) notified.`);
      setHistory(await fetchCheckinHistory(uid));
    } catch (err) {
      console.error("Check-in failed:", err);
      setError(err.message || "Check-in failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const handleCheckOut = async () => {
    setBusy("checkout");
    setError("");
    setMessage("");
    try {
      const result = await runAlert("checkout", {
        Title: activeCheckin.addressTitle,
        Address: activeCheckin.address,
      });
      await setActiveCheckin(uid, null);
      setActive(null);
      setMessage(`Checked out. ${result.sent || 0} contact(s) notified.`);
      setHistory(await fetchCheckinHistory(uid));
    } catch (err) {
      console.error("Check-out failed:", err);
      setError(err.message || "Check-out failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const handleEmergency = async () => {
    if (
      !window.confirm(
        "Send an emergency alert to all your trusted contacts right now?",
      )
    ) {
      return;
    }
    setBusy("emergency");
    setError("");
    setMessage("");
    try {
      // Use the active check-in address if there is one, else the selected one.
      const address = activeCheckin
        ? { Title: activeCheckin.addressTitle, Address: activeCheckin.address }
        : addresses.find((a) => a.id === selectedId);
      const result = await runAlert("emergency", address);
      setMessage(`Emergency alert sent to ${result.sent || 0} contact(s).`);
      setHistory(await fetchCheckinHistory(uid));
    } catch (err) {
      console.error("Emergency alert failed:", err);
      setError(err.message || "Couldn't send the alert. Please try again.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div
      className="container"
      style={{
        width: "85%",
        maxWidth: "420px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <h2>Safety Check-In</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          {noContacts && (
            <div
              style={{
                padding: "12px",
                borderRadius: "10px",
                backgroundColor: "var(--chip-bg, #f0f0f0)",
                marginBottom: "14px",
                fontSize: "14px",
              }}
            >
              Add at least one trusted contact so your check-ins and alerts can
              reach someone.
            </div>
          )}

          {/* Emergency - parked for now (see EMERGENCY_ENABLED). */}
          {EMERGENCY_ENABLED && (
            <button
              type="button"
              onClick={handleEmergency}
              disabled={busy !== "" || noContacts}
              style={{
                width: "100%",
                padding: "16px",
                border: "none",
                cursor: busy !== "" || noContacts ? "default" : "pointer",
                borderRadius: "12px",
                backgroundColor: noContacts ? "#e59aa1" : "#dc3545",
                color: "white",
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "18px",
              }}
            >
              {busy === "emergency" ? "Sending..." : "🚨 Emergency Alert"}
            </button>
          )}

          {message && <p style={{ color: "#28a745" }}>{message}</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          {/* Active check-in, or the check-in control. */}
          {activeCheckin ? (
            <div
              style={{
                padding: "14px",
                borderRadius: "12px",
                border: "1px solid var(--border-color, #ddd)",
                marginBottom: "18px",
              }}
            >
              <div style={{ fontSize: "13px", color: "#28a745" }}>
                Checked in
              </div>
              <div style={{ fontWeight: 600 }}>{activeCheckin.addressTitle}</div>
              <div style={{ fontSize: "14px" }}>{activeCheckin.address}</div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--text-secondary, #777)",
                  marginTop: "4px",
                }}
              >
                Since {formatTime(activeCheckin.startedAt)}
              </div>
              <button
                type="button"
                onClick={handleCheckOut}
                disabled={busy !== ""}
                style={{
                  marginTop: "10px",
                  padding: "10px 24px",
                  border: "none",
                  cursor: busy !== "" ? "default" : "pointer",
                  borderRadius: "5px",
                  backgroundColor: "#007bff",
                  color: "white",
                }}
              >
                {busy === "checkout" ? "Checking out..." : "Check Out"}
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: "18px" }}>
              <label
                htmlFor="checkin-address"
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "14px",
                }}
              >
                Check in at
              </label>
              {addresses.length === 0 ? (
                <p style={{ fontSize: "14px" }}>
                  Add an address first to check in.
                </p>
              ) : (
                <>
                  <select
                    id="checkin-address"
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "12px",
                      border: "1px solid var(--border-color, #ccc)",
                      backgroundColor: "var(--input-bg, #fff)",
                      color: "var(--text-primary, #000)",
                      marginBottom: "10px",
                    }}
                  >
                    {addresses.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.Title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleCheckIn}
                    disabled={busy !== "" || noContacts}
                    style={{
                      padding: "10px 24px",
                      border: "none",
                      cursor:
                        busy !== "" || noContacts ? "default" : "pointer",
                      borderRadius: "5px",
                      backgroundColor: "#4CAF50",
                      color: "white",
                    }}
                  >
                    {busy === "checkin" ? "Checking in..." : "Check In"}
                  </button>
                </>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate("/trusted-contacts")}
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid var(--border-color, #ccc)",
              cursor: "pointer",
              borderRadius: "10px",
              backgroundColor: "transparent",
              color: "var(--text-primary, #333)",
              marginBottom: "18px",
            }}
          >
            Manage Trusted Contacts ({contacts.length})
          </button>

          {/* History */}
          <h3 style={{ fontSize: "16px", marginBottom: "6px" }}>History</h3>
          {history.length === 0 ? (
            <p style={{ fontSize: "14px" }}>No check-ins yet.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {history.map((h) => (
                <li
                  key={h.id}
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid var(--border-color, #eee)",
                    fontSize: "14px",
                  }}
                >
                  <span style={{ textTransform: "capitalize", fontWeight: 600 }}>
                    {h.type}
                  </span>
                  {h.addressTitle ? ` · ${h.addressTitle}` : ""}
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-secondary, #777)",
                    }}
                  >
                    {formatTime(h.createdAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <button
        type="button"
        onClick={() => navigate("/")}
        style={{
          backgroundColor: "gray",
          color: "white",
          padding: "10px 15px",
          border: "none",
          cursor: "pointer",
          borderRadius: "5px",
          marginTop: "18px",
        }}
      >
        Back
      </button>
    </div>
  );
};

export default Safety;
